import { isEmpty, noop, omit } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';

import { BillingLine } from '~hooks/useBillingLines/models';

import { getLineIds, isBillingLineSelectable } from './chargeSelection';

type ChargesCheckedState = boolean | 'intermediate';

type CheckedLinesById = Record<string, BillingLine>;

type ChargesSelectionContextType = {
  checkedLines: BillingLine[];
  getLinesCheckedState: (lines: BillingLine[]) => ChargesCheckedState;
};

type ChargesSelectionActionsContextType = {
  syncCheckedLines: (readLines: BillingLine[], readJobIds: string[]) => void;
  toggleLinesCheck: (lines: BillingLine[]) => void;
  uncheckAllLines: () => void;
  uncheckLines: (lines: BillingLine[]) => void;
};

const ChargesSelectionContext = createContext<ChargesSelectionContextType>({
  checkedLines: [],
  getLinesCheckedState: () => false,
});

const ChargesSelectionActionsContext = createContext<ChargesSelectionActionsContextType>({
  syncCheckedLines: noop,
  toggleLinesCheck: noop,
  uncheckAllLines: noop,
  uncheckLines: noop,
});

function getCheckedState(lines: BillingLine[], checkedLinesById: CheckedLinesById) {
  const selectableLines = lines.filter(isBillingLineSelectable);
  const checkedCount = selectableLines.filter(
    (line) => line.id in checkedLinesById,
  ).length;

  if (checkedCount === 0) {
    return false;
  }

  return checkedCount === selectableLines.length ? true : 'intermediate';
}

// Picks keep the line itself, so they survive their order scrolling out of the list
function ChargesSelectionContextProvider({ children }: React.PropsWithChildren) {
  const [checkedLinesById, setCheckedLinesById] = useState<CheckedLinesById>({});

  const checkedLines = useMemo(() => Object.values(checkedLinesById), [checkedLinesById]);

  const getLinesCheckedState = useCallback(
    (lines: BillingLine[]) => getCheckedState(lines, checkedLinesById),
    [checkedLinesById],
  );

  const toggleLinesCheck = useCallback((lines: BillingLine[]) => {
    const selectableLines = lines.filter(isBillingLineSelectable);

    setCheckedLinesById((state) => {
      const areAllLinesChecked = getCheckedState(lines, state) === true;

      return areAllLinesChecked
        ? omit(state, getLineIds(selectableLines))
        : {
            ...state,
            ...Object.fromEntries(selectableLines.map((line) => [line.id, line])),
          };
    });
  }, []);

  const syncCheckedLines = useCallback(
    (readLines: BillingLine[], readJobIds: string[]) => {
      setCheckedLinesById((state) => {
        const readLineIds = new Set(getLineIds(readLines));
        const readCheckedLines = readLines.filter(
          (line) => line.id !== null && line.id in state,
        );
        const goneCheckedLineIds = getLineIds(
          Object.values(state).filter((line) => {
            const isOnReadJob = Boolean(
              line.scope.job && readJobIds.includes(line.scope.job.id),
            );
            const isMissingFromRead = line.id !== null && !readLineIds.has(line.id);

            return isOnReadJob && isMissingFromRead;
          }),
        );
        const noLongerSelectableLineIds = getLineIds(
          readCheckedLines.filter((line) => !isBillingLineSelectable(line)),
        );
        const changedCheckedLines = readCheckedLines
          .filter(isBillingLineSelectable)
          .filter((line) => state[line.id] !== line);
        const isSelectionUnchanged =
          goneCheckedLineIds.length === 0 &&
          noLongerSelectableLineIds.length === 0 &&
          changedCheckedLines.length === 0;

        if (isSelectionUnchanged) {
          return state;
        }

        return {
          ...omit(state, [...goneCheckedLineIds, ...noLongerSelectableLineIds]),
          ...Object.fromEntries(changedCheckedLines.map((line) => [line.id, line])),
        };
      });
    },
    [],
  );

  const uncheckAllLines = useCallback(
    () => setCheckedLinesById((state) => (isEmpty(state) ? state : {})),
    [],
  );

  const uncheckLines = useCallback((lines: BillingLine[]) => {
    setCheckedLinesById((state) => {
      const checkedLineIds = getLineIds(lines).filter((id) => id in state);

      return checkedLineIds.length === 0 ? state : omit(state, checkedLineIds);
    });
  }, []);

  const selection = useMemo(
    () => ({ checkedLines, getLinesCheckedState }),
    [checkedLines, getLinesCheckedState],
  );

  const actions = useMemo(
    () => ({ syncCheckedLines, toggleLinesCheck, uncheckAllLines, uncheckLines }),
    [syncCheckedLines, toggleLinesCheck, uncheckAllLines, uncheckLines],
  );

  return (
    <ChargesSelectionActionsContext.Provider value={actions}>
      <ChargesSelectionContext.Provider value={selection}>
        {children}
      </ChargesSelectionContext.Provider>
    </ChargesSelectionActionsContext.Provider>
  );
}

export default ChargesSelectionContext;
export { ChargesSelectionActionsContext, ChargesSelectionContextProvider };
